import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { gymApi } from '@/lib/api';
import { sanitizePhone, COUNTRY_CODES } from '@/lib/phone-utils';
import { Client, Status } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ClientDetailSheet } from '@/components/ClientDetailSheet';
import { ClientAvatar } from '@/components/ClientAvatar';
import { ClientPhotoCapture } from '@/components/ClientPhotoCapture';
import { ExcelToolsActions } from '@/components/ExcelToolsActions';
import { buildClientExportRows } from '@/lib/export-utils';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Users,
  QrCode,
  Loader2,
  Building2,
  AlertTriangle,
  Eye
} from 'lucide-react';
import { NoGymSelected } from '@/components/NoGymSelected';

export default function ClientsPage() {
  const { gym, hasRole } = useAuth();
  const isSuperAdmin = hasRole(['super_admin']);
  const canManageExcel = hasRole(['gym_admin', 'gym_supervisor']);
  const [clients, setClients] = useState<any[]>([]); // Changed from Client[] to any[] to handle joined gym data
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [countryCode, setCountryCode] = useState('+503');
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchClients();
  }, [gym?.gym_id]);

  const fetchClients = async () => {
    try {
      // If super admin and no gym selected, we might want to see gym names
      let res;
      if (isSuperAdmin && !gym) {
        // We'll need a special list for Super Admins to see gym names
        // For now, listClients handles undefined gymId by returning all
        res = await gymApi.listClients(null, undefined);
      } else {
        res = await gymApi.listClients(null, gym?.gym_id || undefined);
      }

      if (res.success) {
        setClients(res.clients);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '' as any,
    sex: '',
    status: 'active' as Status,
    emergency_contact: '',
    notes: '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const filteredClients = clients
    .filter(client =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (client.phone && client.phone.includes(searchQuery)) ||
      client.client_id.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else {
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });

  const handleCreateClient = async () => {
    if (gym?.is_trial && clients.length >= 10) {
      toast({
        title: 'Límite de Prueba Alcanzado',
        description: 'En el modo de prueba el límite es de 10 clientes. Actualiza tu plan para registrar socios ilimitados.',
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const preparedData = {
        ...formData,
        phone: sanitizePhone(formData.phone, countryCode)
      };
      const res = await gymApi.createClient(preparedData, null, gym?.gym_id || undefined);
      if (res.success) {
        // La foto se sube después de crear la clienta (ya existe el id).
        if (photoFile && res.client?.client_id) {
          try {
            await gymApi.uploadClientPhoto(res.client.client_id, photoFile, gym?.gym_id || undefined);
          } catch (photoError) {
            console.warn('No se pudo subir la foto de la clienta', photoError);
          }
        }
        await fetchClients();
        setIsCreateOpen(false);
        resetForm();

        toast({
          title: 'Cliente creado',
          description: `${formData.name} ha sido registrado exitosamente`,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error al crear cliente',
        description: error?.message || 'No se pudo crear el cliente',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;

    setIsSubmitting(true);
    try {
      const preparedData = {
        ...formData,
        client_id: editingClient.client_id,
        phone: sanitizePhone(formData.phone, countryCode)
      };
      const res = await gymApi.updateClient(preparedData, null, gym?.gym_id || undefined);
      if (res.success) {
        await fetchClients();
        setEditingClient(null);
        setIsCreateOpen(false); // Close the dialog after update
        resetForm();
        toast({
          title: 'Cliente actualizado',
          description: 'Los cambios han sido guardados',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error al actualizar cliente',
        description: error?.message || 'No se pudo actualizar el cliente',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deletingClient) return;

    setIsSubmitting(true);
    try {
      const res = await gymApi.deleteClient(deletingClient.client_id, null, gym?.gym_id || undefined);
      if (res.success) {
        await fetchClients();
        setDeletingClient(null);
        toast({
          title: 'Cliente eliminado',
          description: 'El cliente ha sido eliminado del sistema',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error al eliminar cliente',
        description: error?.message || 'No se pudo eliminar el cliente',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      age: '' as any,
      sex: '',
      status: 'active',
      emergency_contact: '',
      notes: '',
    });
    setCountryCode('+503');
    setPhotoFile(null);
  };

  const openEditDialog = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      age: client.age || '' as any,
      sex: client.sex || '',
      status: client.status,
      emergency_contact: client.emergency_contact || '',
      notes: client.notes || '',
    });

    // Try to extract country code from phone
    if (client.phone && client.phone.startsWith('+')) {
      const foundCode = COUNTRY_CODES.find(c => client.phone?.startsWith(c.code));
      if (foundCode) {
        setCountryCode(foundCode.code);
        setFormData(prev => ({ ...prev, phone: client.phone?.replace(foundCode.code, '') || '' }));
      } else {
        setCountryCode('+503');
      }
    } else {
      setCountryCode('+503');
    }

    setIsCreateOpen(true);
  };

  const getStatusBadge = (status: Status) => {
    const variants: Record<Status, 'success' | 'warning' | 'destructive' | 'muted'> = {
      active: 'success',
      pending: 'warning',
      inactive: 'muted',
      expired: 'destructive',
    };
    const labels: Record<Status, string> = {
      active: 'Activo',
      pending: 'Pendiente',
      inactive: 'Inactivo',
      expired: 'Expirado',
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Gestión de Clientes">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Gestión de Clientes"
      subtitle={gym ? `Administra los clientes de ${gym.name}` : "Administración global de clientes"}
      actions={
        <div className="flex items-center gap-2">
          {canManageExcel && (
            <ExcelToolsActions
              kind="clients"
              exportRows={buildClientExportRows(filteredClients)}
              exportFileName="clientes"
              importDisabled={!gym}
              onImported={fetchClients}
            />
          )}
          <Dialog open={isCreateOpen} onOpenChange={(open) => {
          if (!gym && !open) { // Prevent opening if no gym for non-super admins? 
            // Actually just disable button.
          }
          setIsCreateOpen(open);
          if (!open) {
            setEditingClient(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button
              variant="gradient"
              disabled={!gym}
              onClick={() => {
                resetForm();
                setEditingClient(null);
                setIsCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Nuevo Cliente</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Editar Cliente' : 'Registrar Nuevo Cliente'}</DialogTitle>
              <DialogDescription>
                {editingClient ? 'Modifica los datos del cliente' : 'Agrega un nuevo cliente al gimnasio'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre completo</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Juan Pérez"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="juan@email.com"
                  disabled={isSubmitting}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <div className="flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger className="w-[100px] font-semibold text-sm">
                        <SelectValue placeholder="País" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="flex items-center gap-2">
                              <span>{c.flag}</span>
                              <span className="text-xs">{c.code}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone"
                      placeholder="8978 8978"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      disabled={isSubmitting}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Edad</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="Ej. 25"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value ? parseInt(e.target.value) : '' as any })}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sexo</Label>
                  <Select
                    value={formData.sex || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, sex: value === 'none' ? '' : value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin especificar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      <SelectItem value="male">Masculino</SelectItem>
                      <SelectItem value="female">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency">Contacto de emergencia</Label>
                <Input
                  id="emergency"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  placeholder="+1 555 789 012"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: Status) => setFormData({ ...formData, status: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="inactive">Inactivo</SelectItem>
                    <SelectItem value="expired">Expirado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionales sobre el cliente"
                  disabled={isSubmitting}
                />
              </div>
              {!editingClient && (
                <ClientPhotoCapture
                  file={photoFile}
                  onFileChange={setPhotoFile}
                  disabled={isSubmitting}
                  label="Foto de perfil"
                  hint="Opcional. Se comprime en el navegador y se sube al crear la clienta."
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateOpen(false);
                setEditingClient(null);
                resetForm();
              }} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button variant="gradient" onClick={editingClient ? handleUpdateClient : handleCreateClient} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editingClient ? 'Guardando...' : 'Creando...'}
                  </>
                ) : (
                  editingClient ? 'Guardar Cambios' : 'Crear Cliente'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      {isSuperAdmin && !gym && <NoGymSelected />}

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar clientes por nombre, email o teléfono..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
                const [field, order] = value.split('-');
                setSortBy(field as any);
                setSortOrder(order as any);
              }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name-asc">Nombre (A-Z)</SelectItem>
                  <SelectItem value="name-desc">Nombre (Z-A)</SelectItem>
                  <SelectItem value="created_at-desc">Recientes primero</SelectItem>
                  <SelectItem value="created_at-asc">Antiguos primero</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingClient} onOpenChange={(open) => !open && setDeletingClient(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>¿Eliminar cliente?</DialogTitle>
            <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg my-2">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="text-xs text-destructive-foreground space-y-1">
                <p className="font-bold">¡Atención! Impacto en el sistema:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Se perderá todo el historial de asistencias y pagos.</li>
                  <li>Los reportes estadísticos históricos se verán afectados.</li>
                  <li>Esta acción es permanente y no se puede deshacer.</li>
                </ul>
                <p className="mt-2 text-[10px] opacity-80 italic">
                  Recomendación: Si solo quieres dar de baja al socio, cámbialo a estado "Inactivo" en lugar de eliminarlo.
                </p>
              </div>
            </div>
            <DialogDescription className="text-center font-medium pt-2">
              ¿Estás seguro de que deseas eliminar permanentemente a <span className="text-foreground font-bold">{deletingClient?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDeletingClient(null)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteClient} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Clientes ({filteredClients.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 lg:p-6">
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Avatar</TableHead>
                  {isSuperAdmin && !gym && <TableHead>Gimnasio</TableHead>}
                  <TableHead>Nombre</TableHead>
                  <TableHead>Edad</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>QR</TableHead>
                  <TableHead>Fecha Registro</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.client_id}>
                    <TableCell>
                      <ClientAvatar name={client.name} photoPath={client.photo_path} className="h-10 w-10" textClassName="text-sm" />
                    </TableCell>
                    {isSuperAdmin && !gym && (
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Building2 className="h-3 w-3" />
                          <span className="text-xs font-medium uppercase tracking-wider">
                            {client.gym_id?.substring(0, 8)}...
                          </span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>
                      {client.age ? (
                        <Badge variant="secondary" className="font-mono">
                          {client.age} años
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs italic">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{client.phone || 'S/T'}</span>
                        <span className="text-xs text-muted-foreground">{client.email || 'S/E'}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(client.status)}</TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-primary">
                            <QrCode className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-xs flex flex-col items-center">
                          <DialogHeader>
                            <DialogTitle>Código QR Access</DialogTitle>
                            <DialogDescription>
                              {client.name}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="bg-white p-4 rounded-xl mt-4">
                            <img
                              src={`https://quickchart.io/qr?text=${encodeURIComponent(client.qr_code)}&size=200`}
                              alt={`QR ${client.name}`}
                              className="w-48 h-48"
                            />
                          </div>
                          <p className="mt-4 font-mono text-xs text-muted-foreground">{client.qr_code}</p>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {client.created_at ? new Date(client.created_at).toLocaleDateString('es-ES') : 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => setSelectedClient(client)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => {
                          openEditDialog(client);
                          setIsCreateOpen(true);
                        }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeletingClient(client)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="block lg:hidden divide-y divide-border">
            {filteredClients.map((client) => (
              <div key={client.client_id} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClientAvatar name={client.name} photoPath={client.photo_path} className="h-10 w-10" textClassName="text-sm" />
                    <div>
                      <div className="flex items-center flex-wrap gap-2">
                        <h3 className="font-semibold text-foreground">{client.name}</h3>
                        {client.age && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 py-0 border-primary/30 bg-primary/5 text-primary font-bold shrink-0 whitespace-nowrap">
                            {client.age} años
                          </Badge>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground uppercase">{client.client_id.substring(0, 8)}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedClient(client)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-primary">
                          <QrCode className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-xs flex flex-col items-center">
                        <DialogHeader>
                          <DialogTitle>Código QR Access</DialogTitle>
                          <DialogDescription>{client.name}</DialogDescription>
                        </DialogHeader>
                        <div className="bg-white p-4 rounded-xl mt-4">
                          <img
                            src={`https://quickchart.io/qr?text=${encodeURIComponent(client.qr_code)}&size=200`}
                            alt={`QR ${client.name}`}
                            className="w-48 h-48"
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                      openEditDialog(client);
                      setIsCreateOpen(true);
                    }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeletingClient(client)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 ml-13">
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase text-muted-foreground font-medium">Contacto</p>
                    <p className="text-xs truncate">{client.email || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground font-medium">{client.phone || 'N/A'}</p>
                  </div>
                  <div className="space-y-1 flex flex-col items-end">
                    <p className="text-[10px] uppercase text-muted-foreground font-medium">Estado</p>
                    {getStatusBadge(client.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredClients.length === 0 && (
            <div className="h-24 flex items-center justify-center text-muted-foreground">
              No se encontraron clientes.
            </div>
          )}
        </CardContent>
      </Card>

      <ClientDetailSheet
        client={selectedClient}
        open={!!selectedClient}
        onOpenChange={(open) => !open && setSelectedClient(null)}
        gymId={gym?.gym_id || ''}
        onPhotoChange={fetchClients}
      />
    </DashboardLayout >
  );
}
